import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import SearchableSelect from '../../../components/SearchableSelect';
import EntityPicker from '../../../components/EntityPicker';
import useEntityPickerData from '../../../hooks/useEntityPickerData';

const ChartFilters = ({ chartFilters, handleFilterChange, handleResetFilters, selectedManufacturer = 'toyota' }) => {
  const entityPickerData = useEntityPickerData(selectedManufacturer);
  const [vehicleModelOptions, setVehicleModelOptions] = useState([]);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accumulatedSites, setAccumulatedSites] = useState([]);

  // Fetch Vehicle Models and Types from API
  useEffect(() => {
    const fetchVehicleData = async () => {
      setLoading(true);
      try {
        const [modelsResponse, typesResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKENDURL}/getvehiclemodels?manufacturer=${selectedManufacturer}`),
          fetch(`${process.env.REACT_APP_BACKENDURL}/getvehicletypes?manufacturer=${selectedManufacturer}`)
        ]);

        const models = await modelsResponse.json();
        const types = await typesResponse.json();

        setVehicleModelOptions(models.map(m => ({ 
          value: String(m.value), 
          label: m.value 
        })));
        setVehicleTypeOptions(types.map(t => ({ 
          value: String(t.value), 
          label: t.value 
        })));
      } catch (error) {
        console.error('Error fetching vehicle data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchVehicleData();
  }, [selectedManufacturer]);

  // Sync accumulated selections to chartFilters (resolve to site IDs)
  React.useEffect(() => {
    const allSiteIds = [];
    
    accumulatedSites.forEach(selection => {
      allSiteIds.push(...selection.siteIds);
    });
    
    // Remove duplicates
    const uniqueSiteIds = [...new Set(allSiteIds)];
    handleFilterChange('siteids', uniqueSiteIds);
  }, [accumulatedSites]);

  // Handle adding current selection to accumulated list
  const handleAddSelection = React.useCallback(() => {
    const filters = entityPickerData.selectedFilters;
    
    // Determine the highest level selected
    let level = null;
    let id = null;
    let label = null;
    let siteIds = [];
    
    if (filters.site) {
      level = 'site';
      id = filters.site;
      const siteOption = entityPickerData.sites.find(s => s.value === filters.site);
      label = siteOption?.label || filters.site;
      siteIds = [filters.site];
    } else if (filters.company) {
      level = 'company';
      id = filters.company;
      const companyOption = entityPickerData.companies.find(c => c.value === filters.company);
      label = companyOption?.label || filters.company;
      // Get all sites under this company
      siteIds = entityPickerData.sites
        .filter(s => s.companyId === filters.company)
        .map(s => s.value);
    } else if (filters.branch) {
      level = 'branch';
      id = filters.branch;
      const branchOption = entityPickerData.branches.find(b => b.value === filters.branch);
      label = branchOption?.label || filters.branch;
      // Get all sites under this branch
      siteIds = entityPickerData.sites
        .filter(s => s.branchId === filters.branch)
        .map(s => s.value);
    } else if (filters.dealer) {
      level = 'dealer';
      id = filters.dealer;
      const dealerOption = entityPickerData.dealers.find(d => d.value === filters.dealer);
      label = dealerOption?.label || filters.dealer;
      // Get all sites under this dealer
      siteIds = entityPickerData.sites
        .filter(s => s.dealerId === filters.dealer)
        .map(s => s.value);
    }
    
    if (!level || !id || siteIds.length === 0) {
      return;
    }

    // Check if already added (same level and id)
    if (accumulatedSites.some(s => s.level === level && s.id === id)) {
      return;
    }

    // Add to accumulated selections
    setAccumulatedSites(prev => [...prev, {
      level,
      id,
      label,
      siteIds,
      siteCount: siteIds.length
    }]);
  }, [entityPickerData, accumulatedSites]);

  // Remove selection from accumulated list
  const handleRemoveSelection = React.useCallback((level, id) => {
    setAccumulatedSites(prev => prev.filter(s => !(s.level === level && s.id === id)));
  }, []);

  // Clear all accumulated selections
  const handleClearAllSelections = React.useCallback(() => {
    setAccumulatedSites([]);
  }, []);

  // Handle reset - clear both chart filters and entity filters
  const handleCompleteReset = React.useCallback(() => {
    handleResetFilters();
    setAccumulatedSites([]);
    entityPickerData.clearAllFilters();
  }, [handleResetFilters, entityPickerData]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0 text-primary">Chart Filters</h5>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleCompleteReset}
          title="Reset all filters to default values"
        >
          Reset
        </Button>
      </div>

      <div className="mb-4 p-3 border rounded bg-light" style={{ borderColor: '#dee2e6' }}>
        <EntityPicker
          selectedFilters={entityPickerData.selectedFilters}
          dealers={entityPickerData.dealers}
          branches={entityPickerData.branches}
          companies={entityPickerData.companies}
          sites={entityPickerData.sites}
          totalCounts={entityPickerData.totalCounts}
          isSearching={entityPickerData.isSearching}
          onFilterChange={entityPickerData.handleFilterChange}
          onSearch={entityPickerData.handleSearch}
          onCancelSearch={entityPickerData.cancelSearch}
          onClearAll={entityPickerData.clearAllFilters}
          loading={entityPickerData.loading}
          error={entityPickerData.error}
          onAddSelection={handleAddSelection}
          accumulatedSelections={accumulatedSites}
          onRemoveSelection={handleRemoveSelection}
          onClearAllSelections={handleClearAllSelections}
        />
      </div>
      
      {/* Vehicle Model */}
      <Form.Group className="mb-3">
        <Form.Label className="fw-bold">Vehicle Model</Form.Label>
        <SearchableSelect
          options={vehicleModelOptions}
          value={chartFilters.vehiclemodels}
          onChange={(value) => handleFilterChange('vehiclemodels', value)}
          placeholder={loading ? "Loading models..." : "Select or search model..."}
          size="sm"
          isMulti={true}
          isDisabled={loading}
        />
      </Form.Group>

      {/* Vehicle Type */}
      <Form.Group className="mb-3">
        <Form.Label className="fw-bold">Vehicle Type</Form.Label>
        <SearchableSelect
          options={vehicleTypeOptions}
          value={chartFilters.vehicletypes}
          onChange={(value) => handleFilterChange('vehicletypes', value)}
          placeholder={loading ? "Loading types..." : "Select or search type..."}
          size="sm"
          isMulti={true}
          isDisabled={loading}
        />
      </Form.Group>
    </div>
  );
};

export default ChartFilters;